<template>
  <div class="sms-wrapper">
    <div class="sms-template">
      <div class="block-title">
        <div class="sms-choice">
          <div
            :class="[
              'sms-choice__default',
              {
                'sms-choice__default_option': option
              }
            ]"
            @click="toggleOptions"
          >
            <i class="icon icon-plus"></i>
            <span>{{ $tr(`vacancy.preferences.sms.insert_variable`) }}</span>
          </div>

          <div v-if="option" class="sms-choice__options">
            <span v-for="item in options" :key="item.value" @click="addWord(item.value)">
              {{ $tr(`vacancy.preferences.sms.${item.text}`) }}
            </span>
          </div>
        </div>

        <span class="chars-count" :class="{ redShake: max_chars_exceeded }">
          <span :class="{ red: max_chars_exceeded }">{{ templateCharsCount }}</span> /
          {{ templateMaxChars }}
        </span>
      </div>

      <textarea
        ref="textarea"
        v-model="template"
        v-validate="'required'"
        :placeholder="$tr('vacancy.preferences.sms.noTemplate')"
        name="template"
        :class="['sms-textarea', { error: errors.has('template') }]"
        @click="option = false"
      ></textarea>
    </div>

    <div class="sms-preview">{{ render_template }}</div>

    <div v-if="canDisabled" class="check-row row-checkbox">
      <trigger
        :value="value.enabled"
        :disabled="!template || isExpried"
        :text="$tr(checkboxpl)"
        @input="set_enabled(!value.enabled)"
      />
    </div>

    <div v-if="isExpried" class="sms-expired">
      <div class="sms-expired-text">{{ $tr('vacancy.preferences.sms.expired') }}</div>

      <vbutton :text="$tr('vacancy.modules.test.send_request')" @click="sendRequest" />
    </div>
  </div>
</template>

<script>
import Axios from 'axios';
import Trigger from '@/components/shared/Trigger';

export default {
  name: 'SmsRow',

  components: {
    Trigger
  },

  props: {
    value: {
      type: Object,
      default: () => {},
      required: true
    },

    index: {
      type: Number,
      required: true
    },

    canDisabled: {
      type: Boolean,
      default: false,
      required: true
    },

    checkboxpl: {
      type: String,
      default: ''
    },

    isExpried: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      option: false,
      options: [
        { text: 'name', value: '{{name}}' },
        { text: 'short_link', value: '{{short link}}' },
        { text: 'vacancy', value: '{{vacancy}}' },
        { text: 'company', value: '{{company}}' },
        { text: 'stages', value: '{{stages}}' },
        { text: 'chatbot', value: '{{chatbot_link}}' }
      ],

      caretPosition: null,
      templateMaxChars: 201
    };
  },

  computed: {
    modules() {
      return this.$store.getters[`vacancy_new/modules/list`];
    },

    enabled: {
      get() {
        return this.value.enabled;
      },

      set(val) {
        this.$emit('change', { id: this.value.id, v: val });
      }
    },

    template: {
      get() {
        return this.value.template;
      },

      set(val) {
        this.$emit('change', { id: this.value.id, template: val });
      }
    },

    render_template() {
      return this.template
        .replace(/{{company}}/gi, this.company_name || '')
        .replace(/{{name}}/gi, this.user_full_name)
        .replace(/{{short link}}/gi, this.short_link)
        .replace(/{{vacancy}}/gi, this.vacancy_name || '')
        .replace(/{{chatbot_link}}/gi, 'https://t.me/xxxx')
        .replace(/{{stages}}/gi, this.stages || '');
    },

    valid() {
      return Object.keys(this.fields)
        .map(k => this.fields[k])
        .every(f => f.valid === true);
    },

    templateCharsCount() {
      return this.render_template ? this.render_template.length : 0;
    },

    current_user() {
      return this.$store.getters['user/current'] || {};
    },

    max_chars_exceeded() {
      return this.render_template.length > this.templateMaxChars;
    },

    user_full_name() {
      return this.current_user ? this.current_user.full_name : '';
    },

    short_link() {
      return `${window.location.origin}/i/xxxxxxxx `;
    },

    company() {
      return this.$store.getters['company/one'];
    },

    company_name() {
      if (!this.company) {
        return '';
      }
      return this.company.name;
    },

    currentLocale() {
      return this.$store.getters['locale/current'];
    },

    vacancy() {
      return this.$store.getters['vacancy/one'];
    },

    vacancy_lang() {
      if (!this.vacancy || !this.vacancy.language) {
        return this.currentLocale;
      }
      return this.vacancy.language.code;
    },

    vacancy_name() {
      if (!this.vacancy) {
        return '';
      }
      return this.vacancy.name;
    },

    stages() {
      if (!this.modules || !this.modules.length) {
        return '';
      }

      let stages = '';
      this.modules.forEach(module => {
        if (!module.is_active) {
          return;
        }
        let module_translate = '';

        if (module.id === 'videointerview') {
          module_translate = this.$tr('vacancies.list.row.modules.module.video.tooltip', this.vacancy_lang);
        } else if (module.id === 'test') {
          module_translate = this.$tr('vacancies.list.row.modules.module.tests.tooltip', this.vacancy_lang);
        }
        if (stages === '') {
          stages = stages + module_translate.toLowerCase();
        } else {
          stages = stages + ` ${this.$tr('common.and', this.vacancy_lang)} ${module_translate.toLowerCase()}`;
        }
      });

      return stages;
    }
  },

  watch: {
    valid(isValid) {
      this.$store.commit('vacancy/sms/templateIsValid', { index: this.index, isValid });
    },

    render_template(val) {
      if (val.length > this.templateMaxChars) {
        this.$store.commit('vacancy/sms/set_max_chars_exceeded', true);
      } else {
        this.$store.commit('vacancy/sms/set_max_chars_exceeded', false);
      }
    }
  },

  mounted() {
    this.$refs.textarea.addEventListener('mouseup', e => {
      this.startCaret(e.target.selectionStart);
    });

    this.$refs.textarea.addEventListener('keyup', e => {
      this.caretPosition = e.target.selectionStart;
    });
  },

  methods: {
    startCaret(num) {
      this.caretPosition = num;
    },

    toggleOptions() {
      this.option = !this.option;
    },

    addWord(word) {
      let startSubstring = this.template.substring(0, this.caretPosition);
      let endSubstring = this.template.substring(this.caretPosition);
      let commonString = `${startSubstring}${word}${endSubstring}`;
      this.template = commonString;
      this.toggleOptions();
    },

    set_enabled(v) {
      this.$emit('change', { id: this.value.id, enabled: v });
    },

    sendRequest() {
      Axios.post('/1.0/company-limits/tariff/request', {
        modules: ['sms']
      })
        .then(() => {
          this.$noty.success(this.$tr('price.popup.success.text'));
        })
        .catch(e => {
          this.$noty.error(e.message + '.');
        });
    }
  }
};
</script>

<style lang="scss" scoped>
@import '@/styles/fonts.scss';

@keyframes shake {
  10%,
  90% {
    transform: translate3d(-1px, 0, 0);
  }

  20%,
  80% {
    transform: translate3d(2px, 0, 0);
  }

  30%,
  50%,
  70% {
    transform: translate3d(-4px, 0, 0);
  }

  40%,
  60% {
    transform: translate3d(4px, 0, 0);
  }
}
.row-checkbox label {
  margin-left: 10px;
}
.sms-template {
  width: 100%;
  border-radius: 8px;
  position: relative;
  margin-bottom: 10px;

  .number {
    width: 40px;
    font-size: 1.6em;
    font-weight: bold;
    color: $green;
  }

  .block-title {
    width: 100%;
    display: flex;
    justify-content: space-between;
    border-top: 1px solid #ccc;
    border-left: 1px solid #ccc;
    border-right: 1px solid #ccc;
    border-radius: 4px 4px 0 0;
    align-items: center;

    .chars-count {
      @include mixin-text-main;
      white-space: nowrap;
      padding: 0 10px;

      span.red {
        color: $red;
      }
    }

    .row-block {
      margin-top: 10px;
    }
  }

  textarea {
    width: 100%;
    max-width: 100%;
    min-width: 100%;
    height: 70px;
    min-height: 3em;
    max-height: 10em;
  }

  .sms-choice {
    position: relative;
    @include mixin-text-main;
    width: 100%;

    &__default {
      display: flex;
      align-items: center;
      padding: 8px 0 8px 10px;
      position: relative;
      cursor: pointer;

      &:hover {
        color: $green;
      }

      &_option {
        color: #ccc;
      }

      .icon {
        font-size: 12px;
        margin-right: 5px;
      }

      span {
        margin-top: 1px;
      }
    }

    &__options {
      position: absolute;
      left: 10px;
      top: 30px;
      border: 1px solid #ccc;
      background: #fff;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
      z-index: 3;
      padding: 4px 8px;
      span {
        display: block;
        padding: 5px 0;
        cursor: pointer;
        min-width: 100%;
        white-space: nowrap;
        padding-right: 20px;

        &:hover {
          color: $green-hover;
        }
      }
    }
  }

  .align-to-top {
    align-items: flex-start;
    justify-content: space-between;
  }

  .check-row {
    margin-top: 18px;
    display: flex;
  }

  .sms-textarea {
    border-radius: 0 0 4px 4px;
  }
}

.sms-wrapper {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-start;
  width: 100%;
}

.sms-preview {
  @include mixin-text-main;
  border-radius: 8px;
  background-color: $grey-light;
  padding: 12px 18px;
  white-space: pre-wrap;
  margin: 20px 0 20px 10px;
  position: relative;
  width: 60%;

  &:before {
    content: '';
    display: block;
    position: absolute;
    border: 1ex solid transparent;
    bottom: 0;
    border-radius: 1em/1ex;
    border-bottom-color: $grey-light;
    border-right-color: $grey-light;
    left: -0.8ex;
  }
}

.sms-expired {
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-sizing: border-box;
  border-radius: 5px;
  background-color: lighten($red-light, 25%);
  padding: 12px 18px;
  font-style: normal;
  font-weight: normal;
  font-size: 15px;
  line-height: 22px;
  color: #233346;
  margin-top: 10px;
}

.sms-expired-text {
  max-width: 65%;
}
</style>
